'use client';

import React, { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Subnet {
  id: string;
  nome: string;
  cidr: string;
  area: string;
  ip_totali: number;
  ip_usati: number;
}

export default function SubnetHealthTable() {
  const [subnets, setSubnets] = useState<Subnet[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase.from('subnets').select('id, nome, cidr, area, ip_totali, ip_usati').order('cidr');
      setSubnets((data || []) as Subnet[]);
      setLoading(false);
    };
    fetch();
  }, []);

  const getHealthColor = (pct: number) => {
    if (pct > 85) return { bar: 'bg-red-500', text: 'text-red-400', label: 'Critico' };
    if (pct > 60) return { bar: 'bg-amber-500', text: 'text-amber-400', label: 'Attenzione' };
    return { bar: 'bg-green-500', text: 'text-green-400', label: 'Sano' };
  };

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <h2 className="text-base font-bold text-white">Stato Sottoreti</h2>
        <span className="text-xs text-muted-color">{subnets.length} subnet monitorate</span>
      </div>

      {loading ? (
        <div className="p-8 flex justify-center">
          <svg className="animate-spin w-6 h-6 text-sky-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-elevated/30">
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Subnet</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Area</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Utilizzo</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">IP Usati</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Stato</th>
              </tr>
            </thead>
            <tbody>
              {subnets.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-secondary-color">Nessuna subnet configurata</td></tr>
              ) : subnets.map((s, idx) => {
                const pct = s.ip_totali > 0 ? Math.round((s.ip_usati / s.ip_totali) * 100) : 0;
                const hc = getHealthColor(pct);
                return (
                  <tr key={s.id} className={`border-b border-border/50 ${idx % 2 !== 0 ? 'bg-surface-elevated/20' : ''} hover:bg-surface-elevated/40 transition-colors`}>
                    <td className="px-5 py-3.5">
                      <div>
                        <p className="font-semibold text-white">{s.nome}</p>
                        <p className="font-mono text-xs text-muted-color">{s.cidr}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-secondary-color capitalize">{s.area}</td>
                    <td className="px-5 py-3.5 w-40">
                      <div className="space-y-1">
                        <div className="h-1.5 bg-surface-elevated rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${hc.bar}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className={`text-xs font-semibold ${hc.text}`}>{pct}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-secondary-color tabular-nums">{s.ip_usati} / {s.ip_totali}</td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs font-semibold ${hc.text}`}>{hc.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}